import React, {useEffect} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import styles from "./TranscriptPage.module.css";
import {
    fetchTranscriptBotTimezone,
    fetchTranscriptById,
    fetchWidgetAppearanceSettings,
    transcriptBotTimezoneRequestStatusSelector,
    transcriptBotTimezoneSelector,
    transcriptRequestStatusSelector,
    transcriptSelector,
    widgetAppearanceSettingsRequestStatusSelector
} from "../transcriptSlice";
import {ChatHistory} from "./chatHistory/ChatHistory";
import {TranscriptPageHeader} from "./header/TranscriptPageHeader";
import {ChatEvents} from "./chatEvents/ChatEvents";
import {ChatEventDTO, ConversationChannel} from "../../../../api/generated";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";

export const TranscriptPage: React.FC = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const transcript = useAppSelector(transcriptSelector);
    const transcriptRequestStatus = useAppSelector(transcriptRequestStatusSelector);
    const widgetAppearanceSettingsRequestStatus = useAppSelector(widgetAppearanceSettingsRequestStatusSelector);
    const botTimezone = useAppSelector(transcriptBotTimezoneSelector);
    const botTimezoneRequestStatus = useAppSelector(transcriptBotTimezoneRequestStatusSelector);

    const {publicBotId, userId} = useParams<{ publicBotId?: string, userId?: string }>();
    const conversationChannel = searchParams.get("conversationChannel") as ConversationChannel

    useEffect(() => {
        if (publicBotId && userId && conversationChannel) {
            dispatch(fetchTranscriptById({userId: Number(userId), conversationChannel}));
            dispatch(fetchWidgetAppearanceSettings(publicBotId));
            dispatch(fetchTranscriptBotTimezone(publicBotId));
            dispatch(updateSelectedMenuItem(MenuItem.TRANSCRIPTS))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicBotId, userId]);

    useEffect(() => {
        if (transcriptRequestStatus === RequestStatus.REJECTED ||
            widgetAppearanceSettingsRequestStatus === RequestStatus.REJECTED ||
            botTimezoneRequestStatus === RequestStatus.REJECTED) {
            navigate("/transcripts")
            dispatch(showErrorToastNotification({message: "Failed to load transcript page"}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcriptRequestStatus, widgetAppearanceSettingsRequestStatus, botTimezoneRequestStatus]);

    if (transcriptRequestStatus === RequestStatus.RESOLVED &&
        widgetAppearanceSettingsRequestStatus === RequestStatus.RESOLVED &&
        botTimezoneRequestStatus === RequestStatus.RESOLVED &&
        transcript) {

        const user = transcript.chatHistoryList[0].user;

        const chatEvents = Array.from(
            new Map(
                transcript.chatHistoryList
                    .flatMap(ch => ch.conversation?.chatEvents ?? [])
                    .map(ce => [ce.id, ce] as const)
            ).values()
        ) as ChatEventDTO[];

        return <div className={styles.wrapper}>
            <TranscriptPageHeader
                user={transcript.chatHistoryList[0].user}
                conversationChannel={transcript.chatHistoryList[0].conversation.channel}
                bot={transcript.bot}
                assistantId={publicBotId}
            />
            <div className={styles.contentWrapper}>
                <ChatHistory
                    chatHistoryList={transcript.chatHistoryList}
                    emailSendingLogs={transcript.emailSendingLogs ?? []}
                    messageFeedback={transcript.messageFeedback ?? []}
                    surveys={transcript.surveys ?? []}
                />
                <ChatEvents
                    user={user}
                    chatEvents={chatEvents}
                    timezone={botTimezone}
                />
            </div>
        </div>
    } else {
        return <Loader/>
    }
}
